import { Menu } from 'antd';
import { observer } from 'mobx-react';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { StatusText } from '@/presentation/components/status-text';
import { TapdTips } from '@/presentation/components/tapd/tapd-tips';
import { useStores } from '@/presentation/store';
import { TapdStatus as TapdItemStatus } from '@/proto/field';

export interface TapdData {
  tapdStatus: string | null;
  tapdUrl: string;
}

export const TapdStatusColor = {
  SendSingleColor: '#3DD598',
  NotSendSingleColor: '#FF974A',
  SendSingleBgColor: '#ebfbf5',
  NotSendSingleBgColor: '#fff5ed',
};

export const TapdStatusMap: { [key: string]: string } = {
  [TapdItemStatus.New]: i18n.t('新'),
  [TapdItemStatus.InProgress]: i18n.t('接受/处理'),
  [TapdItemStatus.Assigned]: i18n.t('已分配'),
  [TapdItemStatus.Resolved]: i18n.t('已解决'),
  [TapdItemStatus.Verified]: i18n.t('已验证'),
  [TapdItemStatus.Reopened]: i18n.t('重新打开'),
  [TapdItemStatus.Rejected]: i18n.t('已拒绝'),
  [TapdItemStatus.Closed]: i18n.t('已关闭'),
  [TapdItemStatus.Unconfirmed]: i18n.t('待确认的'),
  [TapdItemStatus.Planning]: i18n.t('计划中'),
  [TapdItemStatus.Feedback]: i18n.t('需要再次说明'),
  [TapdItemStatus.Acknowledged]: i18n.t('已了解'),
  [TapdItemStatus.Suspended]: i18n.t('挂起'),
  [TapdItemStatus.Postponed]: i18n.t('延期'),
  [TapdItemStatus.TMAudited]: i18n.t('TM审核'),
  [TapdItemStatus.PMMAudited]: i18n.t('PMM审核'),
  [TapdItemStatus.PMAudited]: i18n.t('PM审核'),
  [TapdItemStatus.QAAudited]: i18n.t('QA审核'),
  [TapdItemStatus.Handled]: i18n.t('已处理'),
};

const INIT_PARAMS = {
  status: true,
};

enum TapdStatus {
  View = 'view',     // 查看tapd
  Delete = 'delete', // 取消关联tapd
  Update = 'update', // 重关联tapd
  Create = 'create', // 创建tapd
}

export const TapdStatusTextRender: FC<{
  tapdData: TapdData;
  tapdSubmit: (data?: any) => void;
  rowData?: any;
  pageId: string;
  deleteTapd: () => void;
  updateTapd: () => void;
}> = observer(({ tapdData, tapdSubmit, rowData, deleteTapd, updateTapd, pageId }) => {
  const { t } = useTranslation();
  const [isTapdIssueBtn, setIsTapdIssueBtn] = useState(false);
  const { tapdList, getTapdProductList } = useStores().TapdStore;
  const { productId } = useStores().ProductStore;
  const handleMenu = (e: any) => {
    if (e.key === TapdStatus.View) openTapdLink();
    if (e.key === TapdStatus.Delete) deleteTapd();
    if (e.key === TapdStatus.Update) updateTapd();
    if (e.key === TapdStatus.Create) {
      if (rowData) {
        tapdSubmit(rowData);
        return;
      }
      tapdSubmit();
    }
  };

  const menuList = tapdData.tapdUrl ? [
    {
      name: t('查看'),
      key: TapdStatus.View,
    },
    {
      name: t('重关联'),
      key: TapdStatus.Update,
    },
    {
      name: t('取消关联'),
      key: TapdStatus.Delete,
    },
  ] : [
    {
      name: t('关联缺陷'),
      key: TapdStatus.Update,
    },
    {
      name: t('创建缺陷'),
      key: TapdStatus.Create,
    },
  ];

  const openTapdLink = () => {
    window.open(tapdData.tapdUrl);
    reportClick(pageId, CUSTOM_ACTIONS.commonJumpTapd, { from: pageId });
  };

  const renderMenu = () => (<Menu onClick={handleMenu}>
    {menuList.map(item => <Menu.Item key={item.key}>
      {item.name}
    </Menu.Item>)}
  </Menu>);

  useEffect(() => {
    INIT_PARAMS.status && getTapdProductList();
    INIT_PARAMS.status = false;
    return () => {
      INIT_PARAMS.status = true;
    };
  }, [productId]);

  useEffect(() => {
    setIsTapdIssueBtn(!tapdList.length);
  }, [tapdList]);

  const spanText = tapdData.tapdStatus ? TapdStatusMap[tapdData.tapdStatus] : t('未提单');
  const textColor = !tapdData.tapdUrl ? TapdStatusColor.NotSendSingleColor : TapdStatusColor.SendSingleColor;
  const bgColor = !tapdData.tapdUrl ? TapdStatusColor.NotSendSingleBgColor : TapdStatusColor.SendSingleBgColor;

  const renderStatusText = () => <StatusText
    status={spanText!}
    overlay={renderMenu()}
    textColor={textColor}
    bgColor={bgColor}
    disabled={!tapdList.length}
  />;

  return (
    <div>
      {!isTapdIssueBtn
        ? renderStatusText()
        : <TapdTips>
          <span
            style={{
              color: textColor,
              background: bgColor,
              cursor: 'pointer',
              padding: '5px 8px',
              fontSize: '14px',
            }}
          >
            {spanText}
          </span>
        </TapdTips>}
    </div>
  );
});
